import { Outlet, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  HeartPulse,
  Bell,
  Settings,
  Image,
  MessageCircle,
  Menu,
  Moon,
  Sun,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Pill,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { fetcher } from '../api/client'
import { useAgentStore } from '../store/useAgentStore'

export default function Layout() {
  const { t, i18n } = useTranslation()
  const { user, isDarkMode, isSidebarOpen, toggleDarkMode, toggleSidebar, logout } = useAgentStore()

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/health', icon: HeartPulse, label: t('nav.health') },
    { path: '/medications', icon: Pill, label: t('nav.medications') },
    { path: '/alerts', icon: Bell, label: t('nav.alerts') },
    { path: '/memories', icon: Image, label: t('nav.memories') },
    { path: '/chat', icon: MessageCircle, label: t('nav.chat') },
    { path: '/settings', icon: Settings, label: t('nav.settings') },
  ]

  const isRTL = i18n.language === 'ar'

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Sidebar */}
      <aside
        className={`${
          isSidebarOpen ? 'w-72' : 'w-20'
        } bg-white dark:bg-gray-800 border-e border-gray-200 dark:border-gray-700 
         flex flex-col transition-all duration-300 ease-in-out`}
      >
        {/* Logo */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-gray-200 dark:border-gray-700">
          {isSidebarOpen && (
            <h1 className="text-2xl font-bold text-primary-700 dark:text-primary-400 truncate">
              {t('app.name')}
            </h1>
          )}
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t('sidebar.toggle')}
          >
            {isRTL ? (
              isSidebarOpen ? <ChevronRight size={24} /> : <ChevronLeft size={24} />
            ) : (
              isSidebarOpen ? <ChevronLeft size={24} /> : <ChevronRight size={24} />
            )}
          </button>
        </div>


        {/* Profile Selector */}
        {isSidebarOpen && (
          <ProfileSelector />
        )}

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-4 px-4 py-3 rounded-xl text-lg font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`
              }
            >
              <item.icon size={24} />
              {isSidebarOpen && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
          <button
            onClick={toggleDarkMode}
            className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-gray-600 
                     hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
          >
            {isDarkMode ? <Sun size={24} /> : <Moon size={24} />}
            {isSidebarOpen && <span>{isDarkMode ? t('theme.light') : t('theme.dark')}</span>}
          </button>

          {user && (
            <button
              onClick={logout}
              className="flex items-center gap-4 w-full px-4 py-3 rounded-xl text-danger-600 
                       hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
            >
              <LogOut size={24} />
              {isSidebarOpen && <span>{t('auth.logout')}</span>}
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 
                         flex items-center justify-between px-8">
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <Menu size={24} />
          </button>

          <div className="flex items-center gap-4">
            {user && (
              <div className="text-end">
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{user.full_name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

interface ElderlyProfileOption {
  id: number
  full_name: string
  nickname?: string | null
}

function ProfileSelector() {
  const { selectedElderlyId, setSelectedElderlyId } = useAgentStore()
  const { data: profiles } = useQuery<ElderlyProfileOption[]>({
    queryKey: ['profiles'],
    queryFn: () => fetcher<ElderlyProfileOption[]>('/profiles'),
  })

  if (!profiles || profiles.length === 0) return null

  return (
    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
      <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">
        Elderly Profile
      </label>
      <select
        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-base"
        value={selectedElderlyId || ''}
        onChange={(e) => setSelectedElderlyId(Number(e.target.value) || null)}
        aria-label="Select elderly profile"
      >
        <option value="">— Select —</option>
        {profiles.map((p) => (
          <option key={p.id} value={p.id}>
            {p.nickname || p.full_name}
          </option>
        ))}
      </select>
    </div>
  )
}
